// src/components/inspections/InspectionChecklist.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../contexts/authClient';
import {
  ClipboardList, CheckCircle, XCircle, AlertTriangle, Camera,
  Upload, Loader, Send, ChevronLeft, ChevronRight,
  Check, X, Info, Calendar, Clock, MapPin,
  Plane, Fuel, Wrench, Activity, Gauge, Ruler
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertTriangle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertTriangle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Helper pour formater les erreurs ─────────────────────────────────────────
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  console.error('Full error:', error);
  
  if (error.response?.data) {
    const data = error.response.data;
    
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join('\n• ');
    }
    
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`• ${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`• ${key}: ${value}`);
        } else if (value?.msg) {
          errors.push(`• ${key}: ${value.msg}`);
        }
      });
      if (errors.length > 0) return errors.join('\n');
    }
  }
  
  if (error.message) return error.message;
  return defaultMessage;
};

// ─── Helper Components ──────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const config = {
    pass: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20', label: 'PASS' },
    fail: { icon: XCircle, color: 'text-red-400 bg-red-500/20', label: 'FAIL' },
    warning: { icon: AlertTriangle, color: 'text-yellow-400 bg-yellow-500/20', label: 'WARNING' },
    n_a: { icon: Info, color: 'text-gray-400 bg-gray-500/20', label: 'N/A' },
    deferred: { icon: Clock, color: 'text-orange-400 bg-orange-500/20', label: 'DEFERRED' }
  };
  const c = config[status] || config.warning;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
      <Icon size={10} /> {c.label}
    </span>
  );
};

const BrakingActionSelector = ({ value, onChange }) => (
  <div className="flex gap-2 mt-1">
    {['good', 'medium', 'poor', 'nil'].map((action) => (
      <button
        key={action}
        type="button"
        onClick={() => onChange(action)}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          value === action
            ? action === 'good' ? 'bg-green-500/40 text-white border-green-500'
              : action === 'medium' ? 'bg-yellow-500/40 text-white border-yellow-500'
              : action === 'poor' ? 'bg-orange-500/40 text-white border-orange-500'
              : 'bg-red-500/40 text-white border-red-500'
            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
        } border`}
      >
        {action.toUpperCase()}
      </button>
    ))}
  </div>
);

const PhotoUpload = ({ onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  
  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          if (e.target.files[0]) onUpload(e.target.files[0]);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-1.5 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs text-gray-300 transition disabled:opacity-50"
      >
        {uploading ? <Loader size={12} className="animate-spin" /> : <Camera size={12} />}
        {uploading ? 'Uploading...' : 'Take Photo'}
      </button>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const InspectionChecklist = ({ inspectionId, onClose, onComplete }) => {
  const [inspection, setInspection] = useState(null);
  const [items, setItems] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [findingsSummary, setFindingsSummary] = useState('');
  const [notamNeeded, setNotamNeeded] = useState(false);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch inspection details
  const fetchInspection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/inspections/${inspectionId}`);
      const data = response.data;
      setInspection(data);
      
      const inspectionItems = data.items || [];
      setItems(inspectionItems);
      
      const initialResults = {};
      inspectionItems.forEach(item => {
        if (item.result) {
          initialResults[item.id] = {
            result_status: item.result.result_status,
            value_text: item.result.value_text,
            value_number: item.result.value_number,
            value_boolean: item.result.value_boolean,
            braking_action: item.result.braking_action,
            comment: item.result.comment,
            photo_urls: item.result.photo_urls || []
          };
        } else {
          initialResults[item.id] = {
            result_status: null,
            value_text: '',
            value_number: null,
            value_boolean: false,
            braking_action: null,
            comment: '',
            photo_urls: []
          };
        }
      });
      setResults(initialResults);
    } catch (error) {
      console.error('Error fetching inspection:', error);
      showToast(formatErrorMessage(error, 'Failed to load inspection'), 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [inspectionId, onClose]);

  // Fetch company settings for logo
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await api.get('/company/settings');
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) {
          setLogoUrl(`${API_BASE}${response.data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchInspection();
    fetchCompanySettings();
  }, [fetchInspection, fetchCompanySettings]);

  const updateResult = (itemId, field, value) => {
    setResults(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
        ...(field === 'braking_action' && {
          result_status: value === 'good' ? 'pass' : value === 'medium' ? 'warning' : 'fail'
        })
      }
    }));
  };

  // Upload photo for an item
  const uploadPhoto = async (itemId, file) => {
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(
        `/inspections/${inspectionId}/photos?item_id=${itemId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setResults(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          photo_urls: [...(prev[itemId].photo_urls || []), response.data.photo_url]
        }
      }));
      showToast('Photo uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading photo:', error);
      showToast(formatErrorMessage(error, 'Failed to upload photo'), 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (itemId, photoUrl) => {
    setResults(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        photo_urls: (prev[itemId].photo_urls || []).filter(url => url !== photoUrl)
      }
    }));
  };

  // Submit inspection
  const handleSubmit = async () => {
    const missingRequired = items.filter(item => {
      const result = results[item.id];
      return item.is_critical && (!result || !result.result_status);
    });
    
    if (missingRequired.length > 0) {
      showToast(`Please complete all critical items:\n• ${missingRequired.map(i => i.item_label).join('\n• ')}`, 'warning');
      return;
    }
    
    setSubmitting(true);
    
    const formattedResults = items.map(item => {
      const result = results[item.id] || {};
      return {
        item_id: item.id,
        result_status: result.result_status || 'n_a',
        value_text: result.value_text || null,
        value_number: result.value_number || null,
        value_boolean: result.value_boolean || false,
        braking_action: result.braking_action || null,
        friction_reading: result.value_number || null,
        crack_width_in: result.value_type === 'length_ft' ? result.value_number : null,
        crack_length_ft: null,
        debris_description: result.value_text || null,
        lighting_brightness_percent: result.value_number || null,
        comment: result.comment || null,
        requires_followup: result.result_status === 'fail'
      };
    });
    
    try {
      await api.post(`/inspections/${inspectionId}/complete`, {
        results: formattedResults,
        findings_summary: findingsSummary,
        notam_needed: notamNeeded,
        notam_description: notamNeeded ? findingsSummary : null
      });
      showToast('Inspection completed successfully!', 'success');
      setTimeout(() => onComplete(), 1500);
    } catch (error) {
      console.error('Error completing inspection:', error);
      showToast(formatErrorMessage(error, 'Failed to submit inspection'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const currentItem = items[currentItemIndex];
  const currentResult = currentItem ? results[currentItem.id] : {};
  const isLastItem = currentItemIndex === items.length - 1;
  const isFirstItem = currentItemIndex === 0;
  
  const completedCount = Object.values(results).filter(r => r.result_status).length;
  const progressPercent = items.length > 0 ? (completedCount / items.length) * 100 : 0;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="text-center">
          <Loader size={32} className="animate-spin text-blue-400 mx-auto mb-3" />
          <p className="text-gray-400">Loading inspection checklist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700">
        
        {/* ── Header with Logo ── */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div>
              <h3 className="text-white font-semibold text-base">
                {inspection?.template_name}
              </h3>
              <p className="text-gray-400 text-xs">
                {inspection?.inspection_number} • {inspection?.zone_name || inspection?.zone_type || 'General'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X size={20} />
          </button>
        </div>
        
        {/* ── Progress Bar ── */}
        <div className="px-4 pt-3">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
            <span>Progress</span>
            <span>{Math.round(progressPercent)}% ({completedCount}/{items.length})</span>
          </div>
          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        
        {/* ─── Current Item Card ── */}
        <div className="flex-1 overflow-y-auto p-4">
          {currentItem && (
            <div className="space-y-4">
              {/* Item Header */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    Item {currentItemIndex + 1} of {items.length}
                  </span>
                  {currentItem.is_critical && (
                    <span className="text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full">
                      CRITICAL
                    </span>
                  )}
                  {currentItem.requires_photo && (
                    <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded-full">
                      📷 Photo Required
                    </span>
                  )}
                </div>
                <h4 className="text-white font-semibold text-lg mb-1">
                  {currentItem.item_label}
                </h4>
                {currentItem.item_instruction && (
                  <p className="text-gray-400 text-sm">{currentItem.item_instruction}</p>
                )}
              </div>
              
              {/* Result Status Buttons */}
              <div className="flex flex-wrap gap-2">
                {['pass', 'warning', 'fail', 'n_a', 'deferred'].map(status => (
                  <button
                    key={status}
                    onClick={() => updateResult(currentItem.id, 'result_status', status)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      currentResult.result_status === status
                        ? status === 'pass' ? 'bg-green-500/40 text-white border-green-500'
                          : status === 'warning' ? 'bg-yellow-500/40 text-white border-yellow-500'
                          : status === 'fail' ? 'bg-red-500/40 text-white border-red-500'
                          : 'bg-gray-600 text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    } border`}
                  >
                    {status === 'pass' && <CheckCircle size={12} />}
                    {status === 'warning' && <AlertTriangle size={12} />}
                    {status === 'fail' && <XCircle size={12} />}
                    {status === 'n_a' && <Info size={12} />}
                    {status === 'deferred' && <Clock size={12} />}
                    {status.toUpperCase()}
                  </button>
                ))}
              </div>
              
              {/* Value Input (if requires_value) */}
              {currentItem.requires_value && currentItem.value_type && (
                <div className="space-y-2">
                  <label className="text-gray-400 text-xs block">
                    Value ({currentItem.value_unit || 'value'})
                  </label>
                  {currentItem.value_type === 'boolean' ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateResult(currentItem.id, 'value_boolean', true)}
                        className={`px-3 py-2 rounded-lg text-sm ${currentResult.value_boolean === true ? 'bg-green-500/40 text-white' : 'bg-gray-700 text-gray-400'}`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => updateResult(currentItem.id, 'value_boolean', false)}
                        className={`px-3 py-2 rounded-lg text-sm ${currentResult.value_boolean === false ? 'bg-red-500/40 text-white' : 'bg-gray-700 text-gray-400'}`}
                      >
                        No
                      </button>
                    </div>
                  ) : currentItem.value_type === 'percent' ? (
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={currentResult.value_number || 0}
                        onChange={(e) => updateResult(currentItem.id, 'value_number', parseInt(e.target.value))}
                        className="flex-1"
                      />
                      <span className="text-white text-sm w-12 text-right">
                        {currentResult.value_number || 0}%
                      </span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      step="any"
                      value={currentResult.value_number || ''}
                      onChange={(e) => updateResult(currentItem.id, 'value_number', parseFloat(e.target.value) || null)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                      placeholder={`Enter ${currentItem.value_unit || 'value'}`}
                    />
                  )}
                </div>
              )}
              
              {/* Braking Action (special for runways) */}
              {currentItem.item_code?.startsWith('RWY-BRN') && (
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Braking Action</label>
                  <BrakingActionSelector
                    value={currentResult.braking_action}
                    onChange={(action) => updateResult(currentItem.id, 'braking_action', action)}
                  />
                </div>
              )}
              
              {/* Photo Upload */}
              {currentItem.requires_photo && (
                <div>
                  <label className="text-gray-400 text-xs block mb-2">Photos</label>
                  <PhotoUpload
                    onUpload={(file) => uploadPhoto(currentItem.id, file)}
                    uploading={uploadingPhoto}
                  />
                  {currentResult.photo_urls?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentResult.photo_urls.map((url, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={`${API_BASE}${url}`} 
                            alt={`Photo ${idx + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-600"
                          />
                          <button
                            onClick={() => removePhoto(currentItem.id, url)}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition"
                          >
                            <X size={10} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Comment */}
              <div>
                <label className="text-gray-400 text-xs block mb-1">Comments / Notes</label>
                <textarea
                  value={currentResult.comment || ''}
                  onChange={(e) => updateResult(currentItem.id, 'comment', e.target.value)}
                  rows={2}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Add any additional notes or observations..."
                />
              </div>
            </div>
          )}
        </div>
        
        {/* ─── Navigation Buttons ── */}
        <div className="p-4 border-t border-gray-700 flex justify-between">
          <button
            onClick={() => setCurrentItemIndex(prev => prev - 1)}
            disabled={isFirstItem}
            className="flex items-center gap-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm transition"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          
          {!isLastItem ? (
            <button
              onClick={() => setCurrentItemIndex(prev => prev + 1)}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition"
            >
              Next
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm transition"
            >
              {submitting ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? 'Submitting...' : 'Submit Inspection'}
            </button>
          )}
        </div>
        
        {/* ─── Findings Summary (shown after all items) ── */}
        {isLastItem && (
          <div className="px-4 pb-4">
            <label className="text-gray-400 text-xs block mb-1">Findings Summary (for report)</label>
            <textarea
              value={findingsSummary}
              onChange={(e) => setFindingsSummary(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              placeholder="Summarize any issues found during this inspection..."
            />
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notamNeeded}
                onChange={(e) => setNotamNeeded(e.target.checked)}
                className="w-4 h-4 accent-red-500"
              />
              <span className="text-xs text-gray-400">⚠️ NOTAM required (notify pilots of issue)</span>
            </label>
          </div>
        )}
        
        {/* ── Footer ── */}
        <div className="px-4 py-2 border-t border-gray-800 text-center">
          <p className="text-gray-600 text-[10px]">
            Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
          </p>
        </div>
        
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </div>
  );
};

export default InspectionChecklist;